import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

/**
 * Thin Angular wrapper around the Topaz SigPlusExtLite v3 `Topaz.Global` /
 * `Topaz.Canvas.Sign` API (see sig-plus-ext-lite.d.ts), following the same
 * sequence as Topaz's official "SigPlusExtLiteBasicDemo" for non-LCD pads
 * like this project's SigLite 1x5 (T-S460). Keeps all vendor calls in one
 * place and bridges results back into Angular's zone so template bindings
 * update correctly.
 */

/**
 * The SigPlusExtLite browser extension's content script sets this attribute
 * on <html> to the extension's own wrapper script URL (e.g.
 * "chrome-extension://<id>/SigPlusExtLiteWrapper.js"), which defines
 * `window.Topaz`. Topaz's own demo loader script reads this same attribute
 * and injects the tag via document.write() — but that only works when the
 * loader runs during the page's initial synchronous parse (e.g. a <script>
 * tag in index.html). This service loads on demand instead, well after
 * Angular has booted, and browsers silently drop document.write() calls
 * made from a script inserted that late — so the attribute is read and the
 * real wrapper script tag is injected directly here instead of relying on
 * Topaz's loader file.
 */
const WRAPPER_URL_ATTR = 'SigPlusExtLiteWrapperURL';

@Injectable({ providedIn: 'root' })
export class SignaturePadService {
  private readonly connectedSubject = new BehaviorSubject<boolean>(false);
  private readonly hasSignatureSubject = new BehaviorSubject<boolean>(false);

  readonly connected$ = this.connectedSubject.asObservable();
  readonly hasSignature$ = this.hasSignatureSubject.asObservable();

  private sdkLoadPromise: Promise<boolean> | null = null;

  constructor(private readonly zone: NgZone) {}

  /** True once the SigPlusExtLite extension has injected the Topaz wrapper onto window. */
  isSdkLoaded(): boolean {
    return typeof Topaz !== 'undefined';
  }

  /**
   * Injects the Topaz loader script on demand instead of via index.html, so
   * every page in the app isn't paying for it — only the screens that
   * actually use the signature pad. Safe to call more than once: the same
   * in-flight/settled promise is reused instead of re-injecting the tag.
   */
  loadSdk(): Promise<boolean> {
    if (this.isSdkLoaded()) return Promise.resolve(true);
    if (this.sdkLoadPromise) return this.sdkLoadPromise;

    this.sdkLoadPromise = new Promise<boolean>((resolve) => {
      const wrapperUrl = document.documentElement.getAttribute(WRAPPER_URL_ATTR);
      if (!wrapperUrl) {
        // Extension not installed/enabled, or its content script hasn't run yet.
        resolve(false);
        return;
      }

      const existing = document.querySelector<HTMLScriptElement>(`script[src="${wrapperUrl}"]`);
      if (existing) {
        existing.addEventListener('load', () => resolve(this.isSdkLoaded()));
        existing.addEventListener('error', () => resolve(false));
        return;
      }

      const script = document.createElement('script');
      script.src = wrapperUrl;
      script.addEventListener('load', () => resolve(this.isSdkLoaded()));
      script.addEventListener('error', () => {
        console.error('[Topaz] Failed to load SigPlusExtLite wrapper script.');
        resolve(false);
      });
      document.head.appendChild(script);
    });

    return this.sdkLoadPromise;
  }

  /**
   * Mirrors Topaz's ValidateLCDDemo() check: confirms the extension is
   * installed/enabled and a device is actually attached before signing.
   */
  private async validateDeviceReady(): Promise<{ ok: boolean; reason?: string }> {
    const extensionInstalled = document.documentElement.getAttribute(
      'SigPlusExtLiteExtension-installed'
    );
    if (!extensionInstalled) {
      return { ok: false, reason: 'SigPlusExtLite browser extension is not installed or is disabled.' };
    }

    if (!this.isSdkLoaded()) {
      return { ok: false, reason: 'Topaz wrapper script has not loaded yet.' };
    }

    const status = await Topaz.Global.GetDeviceStatus();
    if (status === 0) {
      return { ok: false, reason: 'No Topaz signature pad is attached.' };
    }
    if (status === -2) {
      return { ok: false, reason: 'SigPlusExtLite SDK is not installed or does not support this feature.' };
    }
    if (status === -3) {
      return { ok: false, reason: 'SigPlus drivers are not installed.' };
    }

    return { ok: true };
  }

  /** Starts a live signing session on the given canvas. */
  async connect(canvas: HTMLCanvasElement): Promise<boolean> {
    const check = await this.validateDeviceReady();
    if (!check.ok) {
      console.error(`[Topaz] ${check.reason}`);
      this.zone.run(() => this.connectedSubject.next(false));
      return false;
    }

    try {
      // Matches Topaz's BasicDemoClearSign(), called before every StartSign
      // in the official demo — resets the device-side capture buffer even
      // on non-LCD pads.
      await this.resetDeviceBuffer();
      await Topaz.Canvas.Sign.StartSign(canvas);
      this.zone.run(() => this.connectedSubject.next(true));
      return true;
    } catch (err) {
      console.error('[Topaz] Failed to start signing session', err);
      this.zone.run(() => this.connectedSubject.next(false));
      return false;
    }
  }

  private async resetDeviceBuffer(): Promise<void> {
    Topaz.Canvas.Sign.ClearSign();
    await Topaz.Canvas.LCDTablet.ClearSigWindow(1);
    await Topaz.Canvas.LCDTablet.ClearTablet();
  }

  /** Ends the signing session and tears down the tablet state. */
  async disconnect(): Promise<void> {
    if (!this.isSdkLoaded()) return;
    await Topaz.Canvas.Sign.StopSign();
    await Topaz.Canvas.Sign.SetTabletState(0);
    this.zone.run(() => this.connectedSubject.next(false));
  }

  async clear(): Promise<void> {
    if (!this.isSdkLoaded()) return;
    await this.resetDeviceBuffer();
    this.zone.run(() => this.hasSignatureSubject.next(false));
  }

  /**
   * Mirrors Topaz's BasicDemoDone() exactly: GetSigString() must be called
   * before GetSignatureImage() — the official demo never gates this on
   * GetTotalPoints(), so this project doesn't either (an earlier version
   * added that guard defensively and it silently blocked every capture).
   * Resolves null only if the extension itself returns no image.
   */
  async captureBase64Image(): Promise<string | null> {
    if (!this.isSdkLoaded()) return null;

    try {
      await Topaz.Canvas.Sign.GetSigString();
      const rawImage = await Topaz.Canvas.Sign.GetSignatureImage();
      if (!rawImage) {
        console.warn('[Topaz] GetSignatureImage() returned empty — sign on the pad before capturing.');
        return null;
      }

      // Topaz's base64 payload commonly contains embedded line breaks, which
      // fails Angular's built-in data-URL sanitizer (it requires the base64
      // segment to be whitespace-free) and silently breaks <img [src]>.
      const cleaned = rawImage.replace(/\s+/g, '');
      const dataUri = this.toDataUri(cleaned);

      this.zone.run(() => this.hasSignatureSubject.next(true));
      return dataUri;
    } catch (err) {
      console.error('[Topaz] Failed to capture signature image', err);
      return null;
    }
  }

  /**
   * On this installed SDK version, GetSignatureImage() returns raw base64
   * with no "data:image/...;base64," prefix at all (confirmed by inspecting
   * the actual rendered <img src> — it started with "/9j/", the standard
   * JPEG signature, and the browser was treating it as a relative URL path).
   * Passes existing full data URIs through untouched in case a different
   * SDK/pad version does include the prefix.
   */
  private toDataUri(base64OrDataUri: string): string {
    if (base64OrDataUri.startsWith('data:')) {
      return base64OrDataUri;
    }

    let mime = 'image/jpeg';
    if (base64OrDataUri.startsWith('iVBORw0KGgo')) {
      mime = 'image/png';
    } else if (base64OrDataUri.startsWith('Qk')) {
      mime = 'image/bmp';
    } else if (base64OrDataUri.startsWith('R0lGOD')) {
      mime = 'image/gif';
    }

    return `data:${mime};base64,${base64OrDataUri}`;
  }
}
